import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import {
  OrganisationProgressClass,
  OrganisationProgressItem,
  OrganisationProgressReport,
  OrganisationProgressSummary,
  OrganisationStudentProgress,
  ProgressItemType,
  ProgressScope,
} from '../../types';
import { formatTimeAgo } from '../../utils/time';
import MarkdownContent from '../shared/MarkdownContent';
import Navbar from '../shared/Navbar';
import StudentProgressDetailModal, { ProgressDetail } from './StudentProgressDetailModal';

const statusLabel = (status: OrganisationStudentProgress['status']) => {
  if (status === 'completed') return 'Completed';
  if (status === 'started') return 'Started';
  return 'Not started';
};

const ClassProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ itemType: string; itemId: string }>();
  const itemType = params.itemType === 'lesson' || params.itemType === 'exam'
    ? params.itemType as ProgressItemType
    : null;
  const itemId = params.itemId || '';
  const [searchParams, setSearchParams] = useSearchParams();
  // Only consulted when classes load; later changes come from the class picker.
  const requestedScopeRef = useRef<string | null>(null);
  requestedScopeRef.current = searchParams.get('class');
  const [item, setItem] = useState<OrganisationProgressItem | null>(null);
  const [classes, setClasses] = useState<OrganisationProgressClass[]>([]);
  const [canViewAllUsers, setCanViewAllUsers] = useState(false);
  const [selectedScope, setSelectedScope] = useState<ProgressScope | null>(null);
  const [students, setStudents] = useState<OrganisationStudentProgress[]>([]);
  const [summary, setSummary] = useState<OrganisationProgressSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [progressLoading, setProgressLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ProgressDetail | null>(null);
  const [detailLoadingUserId, setDetailLoadingUserId] = useState<number | null>(null);
  const [report, setReport] = useState<OrganisationProgressReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(true);
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  // Reports are stored per class, so the all-users scope has none.
  const selectedClassId = typeof selectedScope === 'number' ? selectedScope : null;
  const selectedClassIdRef = useRef<number | null>(null);
  selectedClassIdRef.current = selectedClassId;

  useEffect(() => {
    api.me()
      .then(current => setIsGlobalAdmin(Boolean(current.authenticated && current.user.is_admin)))
      .catch(() => setIsGlobalAdmin(false));
  }, []);

  useEffect(() => {
    if (!itemType || !itemId) {
      setError('Invalid progress page');
      setLoading(false);
      return;
    }
    const loadClasses = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.getOrganisationProgressClasses(itemType, itemId);
        setItem(response.item);
        setClasses(response.classes);
        setCanViewAllUsers(response.can_view_all_users);
        const requestedScope = requestedScopeRef.current;
        const requestedClass = response.classes.find(
          value => String(value.id) === requestedScope,
        );
        const allUsersScope = response.can_view_all_users ? 'all' : null;
        setSelectedScope(
          requestedClass?.id
          ?? (requestedScope === 'all' ? allUsersScope : null)
          ?? allUsersScope
          ?? response.classes[0]?.id
          ?? null,
        );
      } catch (err: any) {
        setError(err?.message || 'Failed to load classes');
      } finally {
        setLoading(false);
      }
    };
    void loadClasses();
  }, [itemId, itemType]);

  useEffect(() => {
    setReport(null);
    setReportError(null);
    setReportOpen(true);
    if (!itemType || !itemId || selectedClassId === null) return;
    let cancelled = false;
    api.getOrganisationClassProgressReport(itemType, itemId, selectedClassId)
      .then(response => {
        if (!cancelled) setReport(response.report);
      })
      .catch((err: any) => {
        if (!cancelled) setReportError(err?.message || 'Failed to load report');
      });
    return () => {
      cancelled = true;
    };
  }, [itemId, itemType, selectedClassId]);

  useEffect(() => {
    if (!itemType || !itemId || selectedScope === null) {
      setStudents([]);
      setSummary(null);
      return;
    }
    const loadProgress = async () => {
      try {
        setProgressLoading(true);
        setError(null);
        const response = await api.getOrganisationClassProgress(
          itemType,
          itemId,
          selectedScope,
        );
        setStudents(response.students);
        setSummary(response.summary);
      } catch (err: any) {
        setError(err?.message || 'Failed to load class progress');
      } finally {
        setProgressLoading(false);
      }
    };
    void loadProgress();
  }, [itemId, itemType, selectedScope]);

  const generateReport = async () => {
    if (!itemType || !itemId || selectedClassId === null) return;
    const classId = selectedClassId;
    try {
      setReportLoading(true);
      setReportError(null);
      const response = await api.generateOrganisationClassProgressReport(itemType, itemId, classId);
      // Ignore a report that finishes after the teacher switched class.
      if (selectedClassIdRef.current === classId) {
        setReport(response.report);
        setReportOpen(true);
      }
    } catch (err: any) {
      if (selectedClassIdRef.current === classId) {
        setReportError(err?.message || 'Failed to generate report');
      }
    } finally {
      setReportLoading(false);
    }
  };

  const canGenerateReport = selectedClassId !== null
    && !!summary
    && summary.started_count > 0
    && !progressLoading;

  const openStudentDetail = async (student: OrganisationStudentProgress) => {
    if (!itemType || !itemId || selectedScope === null || student.status === 'not_started') return;
    try {
      setDetailLoadingUserId(student.user_id);
      setError(null);
      setDetail(await api.getOrganisationStudentProgressDetail(
        itemType,
        itemId,
        selectedScope,
        student.user_id,
      ));
    } catch (err: any) {
      setError(err?.message || 'Failed to load student detail');
    } finally {
      setDetailLoadingUserId(null);
    }
  };

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container class-progress-page">
          <button type="button" className="tm-button tm-button--secondary org-secondary-btn" onClick={() => navigate(-1)}>
            Back to course
          </button>
          <div className="page-intro">
            <span className="class-progress-eyebrow">{item?.type === 'exam' ? 'Exercise progress' : 'Lesson progress'}</span>
            <h2>{item?.title || 'Student progress'}</h2>
            <p>
              {canViewAllUsers
                ? 'Review the latest activity of every user, or select a class.'
                : 'Select a class to review each student’s latest activity.'}
            </p>
          </div>

          {error && <div className="org-error" role="alert">{error}</div>}
          {loading ? (
            <div className="loading">Loading progress…</div>
          ) : classes.length === 0 && !canViewAllUsers ? (
            <div className="ml-empty">You do not have an eligible class with access to this item.</div>
          ) : (
            <>
              <section className="tm-surface class-progress-toolbar">
                <label>
                  Class
                  <select
                    value={selectedScope ?? ''}
                    onChange={event => {
                      const value = event.target.value;
                      setSelectedScope(value === 'all' ? 'all' : Number(value));
                      setSearchParams({ class: value }, { replace: true });
                    }}
                  >
                    {canViewAllUsers && <option value="all">All users</option>}
                    {classes.map(value => (
                      <option key={value.id} value={value.id}>
                        {canViewAllUsers ? `${value.organisation_name} · ` : ''}
                        {value.name} ({value.student_count} students)
                      </option>
                    ))}
                  </select>
                </label>
                {selectedClassId !== null && (
                  <button
                    type="button"
                    className="tm-button tm-button--primary"
                    onClick={() => void generateReport()}
                    disabled={!canGenerateReport || reportLoading}
                    title={canGenerateReport ? undefined : 'No students have started yet'}
                  >
                    {reportLoading ? 'Generating report…' : report ? 'Regenerate report' : 'Generate report'}
                  </button>
                )}
              </section>

              {reportError && <div className="org-error" role="alert">{reportError}</div>}
              {reportLoading && !report && (
                <div className="loading">
                  Analysing {item?.type === 'exam' ? 'marked answers' : 'student conversations'}… this can take a minute.
                </div>
              )}
              {report && (
                <section className="tm-surface class-progress-report" aria-label="Class report">
                  <button
                    type="button"
                    className="class-progress-report-header"
                    aria-expanded={reportOpen}
                    aria-controls="class-progress-report-body"
                    onClick={() => setReportOpen(open => !open)}
                  >
                    <span className="class-progress-report-title">
                      <span className="class-progress-report-chevron" aria-hidden="true">▸</span>
                      Where the class got stuck
                    </span>
                    <span className="class-progress-report-meta">
                      Based on {report.students_included}{' '}
                      {report.students_included === 1 ? 'student' : 'students'} · generated{' '}
                      {formatTimeAgo(report.generated_at)}
                      {report.generated_by_email && ` by ${report.generated_by_email}`}
                    </span>
                  </button>
                  {reportOpen && (
                    <div id="class-progress-report-body" className="class-progress-report-body">
                      <MarkdownContent content={report.report} />
                    </div>
                  )}
                </section>
              )}

              {summary && (
                <section className="class-progress-summary" aria-label="Class progress summary">
                  <div><strong>{summary.student_count}</strong><span>Students</span></div>
                  <div><strong>{summary.started_count}</strong><span>Started</span></div>
                  <div><strong>{summary.completed_count}</strong><span>Completed</span></div>
                </section>
              )}

              {progressLoading ? (
                <div className="loading">Loading students…</div>
              ) : students.length === 0 ? (
                <div className="ml-empty">
                  {selectedScope === 'all'
                    ? `No one has started this ${item?.type ?? 'item'} yet.`
                    : 'This class has no student members.'}
                </div>
              ) : (
                <section className="class-progress-list">
                  <div className="class-progress-list-header">
                    <span>Student</span><span>Status</span><span>Latest activity</span><span>Result</span>
                  </div>
                  {students.map(student => (
                    <button
                      key={student.user_id}
                      type="button"
                      className="class-progress-row"
                      onClick={() => void openStudentDetail(student)}
                      disabled={student.status === 'not_started' || detailLoadingUserId !== null}
                    >
                      <span className="class-progress-student">{student.email}</span>
                      <span className={`class-progress-status class-progress-status--${student.status}`}>
                        {statusLabel(student.status)}
                      </span>
                      <span>
                        {student.latest_activity_at
                          ? formatTimeAgo(student.latest_activity_at)
                          : 'No activity'}
                      </span>
                      <span>
                        {student.obtained_marks !== null && student.obtained_marks !== undefined
                          ? `${student.obtained_marks} / ${student.total_marks ?? '—'}`
                          : student.latest_attempt_state?.replace('_', ' ') || '—'}
                      </span>
                    </button>
                  ))}
                </section>
              )}
            </>
          )}
        </div>
      </main>

      {detail && (
        <StudentProgressDetailModal
          detail={detail}
          scope={selectedScope ?? undefined}
          isGlobalAdmin={isGlobalAdmin}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
};

export default ClassProgressPage;
